import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button, Modal, Form, Table, Badge } from "react-bootstrap";
import Header from "../header/header";

const API_BASE = "http://localhost:5000/api/admin/products";
const IMAGE_BASE = "http://localhost:5000";

const getAdminAxiosConfig = () => {
  const token = localStorage.getItem("adminToken");
  return {
    headers: { Authorization: token ? `Bearer ${token}` : "" },
  };
};

function AdminProduct() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  
  // Simple state for form data - no JSON complexity
  const [formData, setFormData] = useState({
    ProductName: "",
    ProductPrice: "",
    Category: "",
    Description: "",
    Brand: "",
    Stock: "",
    Discount: ""
  });
  
  // Arrays for dynamic fields
  const [colors, setColors] = useState([]);
  const [currentColor, setCurrentColor] = useState({ name: "", hex: "#000000" });
  
  const [features, setFeatures] = useState([]);
  const [currentFeature, setCurrentFeature] = useState("");
  
  const [variants, setVariants] = useState([]);
  const [currentVariant, setCurrentVariant] = useState({ color: "", price: "", stock: "" });
  
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleUnauthorized = () => {
    alert("Session expired. Please login again.");
    localStorage.clear();
    window.location.href = "/login";
  };

  const fetchProducts = async () => {
    try {
      const res = await axios.get(API_BASE, getAdminAxiosConfig());
      const data = res.data.products || [];
      setProducts(data);
      const uniqueCategories = [...new Set(data.map((p) => p.category).filter(Boolean))];
      setCategories(uniqueCategories);
    } catch (error) {
      if (error.response?.status === 401) handleUnauthorized();
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);
    setImageFile(file);
    setImagePreview(previewUrl);
  };

  // Color management
  const addColor = () => {
    if (currentColor.name.trim()) {
      setColors([...colors, { ...currentColor }]);
      setCurrentColor({ name: "", hex: "#000000" });
    }
  };

  const removeColor = (index) => {
    setColors(colors.filter((_, i) => i !== index));
  };

  // Feature management
  const addFeature = () => {
    if (currentFeature.trim()) {
      setFeatures([...features, currentFeature]);
      setCurrentFeature("");
    }
  };

  const removeFeature = (index) => {
    setFeatures(features.filter((_, i) => i !== index));
  };

  // Variant management
  const addVariant = () => {
    if (currentVariant.color && currentVariant.price) {
      setVariants([...variants, { ...currentVariant }]);
      setCurrentVariant({ color: "", price: "", stock: "" });
    }
  };

  const removeVariant = (index) => {
    setVariants(variants.filter((_, i) => i !== index));
  };

  const openAddModal = () => {
    setEditMode(false);
    setFormData({
      ProductName: "",
      ProductPrice: "",
      Category: "",
      Description: "",
      Brand: "",
      Stock: "",
      Discount: ""
    });
    setColors([]);
    setFeatures([]);
    setVariants([]);
    setImageFile(null);
    setImagePreview("");
    setShowModal(true);
  };

  const openEditModal = (product) => {
    setEditMode(true);
    setSelectedId(product._id);
    
    setFormData({
      ProductName: product.name || "",
      ProductPrice: product.price || "",
      Category: product.category || "",
      Description: product.description || "",
      Brand: product.brand || "",
      Stock: product.stock || "",
      Discount: product.discount || ""
    });
    
    // Parse existing data
    setColors(product.colors || []);
    setFeatures(product.features || []);
    setVariants(product.variants || []);
    setImagePreview(product.images?.[0] || "");
    setImageFile(null);
    
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();

    try {
      const data = new FormData();

      // Basic fields
      data.append("name", formData.ProductName);
      data.append("price", formData.ProductPrice);
      data.append("category", formData.Category);
      data.append("brand", formData.Brand);
      data.append("description", formData.Description || "");
      data.append("stock", formData.Stock || "0");
      data.append("discount", formData.Discount || "0");

      // Send arrays as JSON strings (backend expects this)
      if (colors.length > 0) {
        data.append("colors", JSON.stringify(colors));
      }
      
      if (features.length > 0) {
        data.append("features", JSON.stringify(features));
      }
      
      if (variants.length > 0) {
        data.append("variants", JSON.stringify(variants));
      }

      // Image
      if (imageFile) {
        data.append("images", imageFile);
      }

      const config = {
        headers: {
          Authorization: getAdminAxiosConfig().headers.Authorization,
        },
      };

      if (editMode) {
        await axios.put(`${API_BASE}/${selectedId}`, data, config);
      } else {
        await axios.post(API_BASE, data, config);
      }

      fetchProducts();
      setShowModal(false);
      alert(editMode ? "Product updated successfully!" : "Product added successfully!");

    } catch (error) {
      console.error("❌ FULL ERROR:", error.response?.data || error.message);
      alert(error.response?.data?.message || "Error saving product");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await axios.delete(`${API_BASE}/${id}`, getAdminAxiosConfig());
      fetchProducts();
    } catch (error) {
      if (error.response?.status === 401) handleUnauthorized();
      else console.error("Delete Product Error:", error);
    }
  };

  const getImageUrl = (images) => {
    if (!images) return "https://via.placeholder.com/100";
    if (Array.isArray(images)) {
      const img = images[0];
      if (!img) return "https://via.placeholder.com/100";
      if (img.startsWith("http") || img.startsWith("blob:")) return img;
      return `${img}`;
    }
    if (typeof images === "string") {
      if (images.startsWith("http") || images.startsWith("blob:")) return images;
      return `${images}`;
    }
    return "https://via.placeholder.com/100";
  };

  return (
    <div>
      <Header />
      <div className="container mt-4">
        <Button onClick={openAddModal} className="mb-3">+ Add Product</Button>

        <Table striped bordered className="mt-3">
          <thead>
            <tr>
              <th>Image</th>
              <th>Name</th>
              <th>Price</th>
              <th>Category</th>
              <th>Stock</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.length === 0 && (
              <tr>
                <td colSpan="6" className="text-center">No Products</td>
              </tr>
            )}
            {products.map((p) => (
              <tr key={p._id}>
                <td>
                  <img
                    src={getImageUrl(p.images)}
                    alt={p.name}
                    width="60"
                    height="60"
                    style={{ objectFit: "cover" }}
                  />
                </td>
                <td>{p.name}</td>
                <td>₹{p.price}</td>
                <td>{p.category}</td>
                <td>{p.stock || 0}</td>
                <td>
                  <Button size="sm" onClick={() => openEditModal(p)}>Edit</Button>{" "}
                  <Button size="sm" variant="danger" onClick={() => handleDelete(p._id)}>Delete</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {/* Add/Edit Modal */}
        <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{editMode ? "Edit Product" : "Add Product"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleSave}>
              {/* Basic Information Section */}
              <h5 className="mb-3">Basic Information</h5>
              
              {imagePreview && (
                <div className="text-center mb-3">
                  <img src={imagePreview} alt="Preview" width="150" height="150" style={{ objectFit: "cover" }} />
                </div>
              )}
              
              <Form.Group className="mb-3">
                <Form.Label>Product Image *</Form.Label>
                <Form.Control type="file" accept="image/*" onChange={handleFileSelect} />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Product Name *</Form.Label>
                <Form.Control name="ProductName" value={formData.ProductName} onChange={handleChange} required />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Brand *</Form.Label>
                <Form.Control name="Brand" value={formData.Brand} onChange={handleChange} required />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Price *</Form.Label>
                <Form.Control name="ProductPrice" type="number" value={formData.ProductPrice} onChange={handleChange} required />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Stock Quantity *</Form.Label>
                <Form.Control name="Stock" type="number" value={formData.Stock} onChange={handleChange} required />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Discount (%)</Form.Label>
                <Form.Control name="Discount" type="number" value={formData.Discount} onChange={handleChange} />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Category *</Form.Label>
                <Form.Select name="Category" value={formData.Category} onChange={handleChange} required>
                  <option value="">-- Select Category --</option>
                  {categories.map((c, idx) => (
                    <option key={idx} value={c}>{c}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control as="textarea" rows={3} name="Description" value={formData.Description} onChange={handleChange} />
              </Form.Group>

              {/* Colors Section */}
              <h5 className="mb-3 mt-4">Available Colors</h5>
              <div className="border p-3 rounded mb-3">
                <div className="row mb-2">
                  <div className="col-md-6">
                    <Form.Control
                      placeholder="Color name (e.g., Red, Blue)"
                      value={currentColor.name}
                      onChange={(e) => setCurrentColor({ ...currentColor, name: e.target.value })}
                    />
                  </div>
                  <div className="col-md-4">
                    <Form.Control
                      type="color"
                      value={currentColor.hex}
                      onChange={(e) => setCurrentColor({ ...currentColor, hex: e.target.value })}
                    />
                  </div>
                  <div className="col-md-2">
                    <Button variant="success" onClick={addColor} className="w-100">Add</Button>
                  </div>
                </div>
                
                <div className="mt-2">
                  {colors.map((color, idx) => (
                    <Badge key={idx} bg="secondary" className="me-2 mb-2 p-2">
                      <span style={{ backgroundColor: color.hex, display: 'inline-block', width: '15px', height: '15px', marginRight: '5px', borderRadius: '3px' }}></span>
                      {color.name}
                      <Button size="sm" variant="danger" className="ms-2 py-0" onClick={() => removeColor(idx)}>×</Button>
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Features Section */}
              <h5 className="mb-3">Product Features</h5>
              <div className="border p-3 rounded mb-3">
                <div className="mb-2">
                  <div className="input-group">
                    <Form.Control
                      placeholder="Add a feature (e.g., Water Resistant)"
                      value={currentFeature}
                      onChange={(e) => setCurrentFeature(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addFeature()}
                    />
                    <Button variant="success" onClick={addFeature}>Add Feature</Button>
                  </div>
                </div>
                
                <div className="mt-2">
                  {features.map((feature, idx) => (
                    <Badge key={idx} bg="info" className="me-2 mb-2 p-2">
                      ✓ {feature}
                      <Button size="sm" variant="danger" className="ms-2 py-0" onClick={() => removeFeature(idx)}>×</Button>
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Variants Section */}
              <h5 className="mb-3">Product Variants (Optional)</h5>
              <div className="border p-3 rounded mb-3">
                <div className="row mb-2">
                  <div className="col-md-4">
                    <Form.Control
                      placeholder="Color"
                      value={currentVariant.color}
                      onChange={(e) => setCurrentVariant({ ...currentVariant, color: e.target.value })}
                    />
                  </div>
                  <div className="col-md-3">
                    <Form.Control
                      type="number"
                      placeholder="Price"
                      value={currentVariant.price}
                      onChange={(e) => setCurrentVariant({ ...currentVariant, price: e.target.value })}
                    />
                  </div>
                  <div className="col-md-3">
                    <Form.Control
                      type="number"
                      placeholder="Stock"
                      value={currentVariant.stock}
                      onChange={(e) => setCurrentVariant({ ...currentVariant, stock: e.target.value })}
                    />
                  </div>
                  <div className="col-md-2">
                    <Button variant="success" onClick={addVariant} className="w-100">Add</Button>
                  </div>
                </div>
                
                {variants.length > 0 && (
                  <table className="table table-sm mt-2">
                    <thead>
                      <tr>
                        <th>Color</th>
                        <th>Price</th>
                        <th>Stock</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {variants.map((variant, idx) => (
                        <tr key={idx}>
                          <td>{variant.color}</td>
                          <td>₹{variant.price}</td>
                          <td>{variant.stock || 0}</td>
                          <td>
                            <Button size="sm" variant="danger" onClick={() => removeVariant(idx)}>Remove</Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <Button type="submit" variant="primary" className="mt-3">
                {editMode ? "Update Product" : "Save Product"}
              </Button>
            </Form>
          </Modal.Body>
        </Modal>
      </div>
    </div>
  );
}

export default AdminProduct;